// Voice-note recorder — a framework-free state machine around MediaRecorder so
// the flow is unit-testable (the React hook `use-voice-note-recorder` is a thin
// subscription wrapper). States:
//
//   idle → recording → preview → idle
//                 ↘ (cancel) ↗
//   any → error (mic denied / unsupported / recorder failure) → idle (dismiss)
//
// Recording prefers audio/mp4 (AAC) — the one container every consumer of the
// note can decode, including iOS AVPlayer, which has no webm/opus decoder —
// falling back to webm/opus where mp4 recording is unsupported (Firefox; those
// notes still transcribe and play on web/Android). A denied microphone lands in a quiet
// `error` state — the UI shows an inline notice, never a modal.

export type VoiceNoteStatus = 'idle' | 'starting' | 'recording' | 'preview' | 'error';
export type VoiceNoteErrorKind = 'denied' | 'unsupported' | 'failed';

export interface VoiceNoteClip {
  blob: Blob;
  // Base mime with any codec suffix stripped (`audio/webm`, `audio/mp4`) —
  // the wire mimeType for the attachment and upload-url mint.
  mimeType: string;
  // Elapsed recording time in whole seconds when Stop was pressed.
  durationSeconds: number;
}

export interface VoiceNoteState {
  status: VoiceNoteStatus;
  seconds: number;
  clip: VoiceNoteClip | null;
  errorKind: VoiceNoteErrorKind | null;
}

const PREFERRED_MIME_TYPES = ['audio/mp4', 'audio/webm;codecs=opus', 'audio/webm'] as const;

type MediaRecorderLike = {
  state: string;
  mimeType?: string;
  start: (timeslice?: number) => void;
  stop: () => void;
  ondataavailable: ((e: { data: Blob }) => void) | null;
  onstop: (() => void) | null;
  onerror: ((e: unknown) => void) | null;
};

type MediaRecorderCtor = {
  new (stream: MediaStream, options?: { mimeType?: string }): MediaRecorderLike;
  isTypeSupported?: (type: string) => boolean;
};

export interface VoiceNoteRecorderDeps {
  getUserMedia: (constraints: MediaStreamConstraints) => Promise<MediaStream>;
  mediaRecorder: MediaRecorderCtor | null;
  // Injectable clock for tests; defaults to setInterval/clearInterval.
  setInterval?: (fn: () => void, ms: number) => ReturnType<typeof setInterval>;
  clearInterval?: (handle: ReturnType<typeof setInterval>) => void;
}

export function defaultVoiceNoteDeps(): VoiceNoteRecorderDeps {
  const nav = typeof navigator !== 'undefined' ? navigator : undefined;
  const Recorder =
    typeof window !== 'undefined' ? ((window as unknown as { MediaRecorder?: MediaRecorderCtor }).MediaRecorder ?? null) : null;
  return {
    getUserMedia: (constraints) => {
      if (!nav?.mediaDevices?.getUserMedia) return Promise.reject(new Error('getUserMedia unavailable'));
      return nav.mediaDevices.getUserMedia(constraints);
    },
    mediaRecorder: Recorder,
  };
}

export function pickRecordingMimeType(Recorder: MediaRecorderCtor | null): string | null {
  if (!Recorder) return null;
  const supports = Recorder.isTypeSupported;
  if (!supports) return PREFERRED_MIME_TYPES[0];
  for (const type of PREFERRED_MIME_TYPES) {
    try {
      if (supports.call(Recorder, type)) return type;
    } catch {
      // treat a throwing probe as unsupported
    }
  }
  return null;
}

// Strip the `;codecs=…` suffix — the attachment's wire mimeType is the base type.
export function baseMimeType(mimeType: string): string {
  return mimeType.split(';')[0].trim() || 'audio/webm';
}

const isPermissionDenied = (err: unknown): boolean => {
  const name = (err as { name?: string } | null)?.name;
  return name === 'NotAllowedError' || name === 'SecurityError' || name === 'PermissionDeniedError';
};

export class VoiceNoteRecorder {
  private readonly deps: VoiceNoteRecorderDeps;
  private listeners = new Set<(state: VoiceNoteState) => void>();
  private state: VoiceNoteState = { status: 'idle', seconds: 0, clip: null, errorKind: null };
  private recorder: MediaRecorderLike | null = null;
  private stream: MediaStream | null = null;
  private chunks: Blob[] = [];
  private timer: ReturnType<typeof setInterval> | null = null;
  private mimeType = 'audio/webm';
  // Set by cancel() so the onstop handler discards instead of previewing.
  private discardOnStop = false;

  constructor(deps: VoiceNoteRecorderDeps = defaultVoiceNoteDeps()) {
    this.deps = deps;
  }

  getState(): VoiceNoteState {
    return this.state;
  }

  subscribe(listener: (state: VoiceNoteState) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private setState(patch: Partial<VoiceNoteState>): void {
    this.state = { ...this.state, ...patch };
    this.listeners.forEach((l) => l(this.state));
  }

  async start(): Promise<void> {
    if (this.state.status === 'recording' || this.state.status === 'starting') return;
    const picked = pickRecordingMimeType(this.deps.mediaRecorder);
    if (!this.deps.mediaRecorder || !picked) {
      this.setState({ status: 'error', errorKind: 'unsupported', clip: null, seconds: 0 });
      return;
    }
    this.setState({ status: 'starting', seconds: 0, clip: null, errorKind: null });
    let stream: MediaStream;
    try {
      stream = await this.deps.getUserMedia({ audio: true });
    } catch (err) {
      this.setState({ status: 'error', errorKind: isPermissionDenied(err) ? 'denied' : 'failed' });
      return;
    }
    // A cancel that raced the permission prompt: release the mic and stay idle.
    // (Read through getState() — the awaited gap invalidates TS's narrowing.)
    if (this.getState().status !== 'starting') {
      stream.getTracks().forEach((t) => t.stop());
      return;
    }
    this.stream = stream;
    this.chunks = [];
    this.discardOnStop = false;
    this.mimeType = picked;
    let recorder: MediaRecorderLike;
    try {
      recorder = new this.deps.mediaRecorder(stream, { mimeType: picked });
    } catch {
      this.releaseStream();
      this.setState({ status: 'error', errorKind: 'failed' });
      return;
    }
    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) this.chunks.push(e.data);
    };
    recorder.onerror = () => {
      this.discardOnStop = true;
      this.stopTimer();
      this.releaseStream();
      this.recorder = null;
      this.setState({ status: 'error', errorKind: 'failed', clip: null });
    };
    recorder.onstop = () => {
      this.stopTimer();
      this.releaseStream();
      this.recorder = null;
      if (this.discardOnStop) {
        this.setState({ status: 'idle', seconds: 0, clip: null, errorKind: null });
        return;
      }
      const mime = baseMimeType(recorder.mimeType || this.mimeType);
      const blob = new Blob(this.chunks, { type: mime });
      this.chunks = [];
      if (blob.size === 0) {
        this.setState({ status: 'error', errorKind: 'failed', clip: null });
        return;
      }
      this.setState({ status: 'preview', clip: { blob, mimeType: mime, durationSeconds: this.state.seconds } });
    };
    try {
      recorder.start(250);
    } catch {
      this.releaseStream();
      this.setState({ status: 'error', errorKind: 'failed' });
      return;
    }
    this.recorder = recorder;
    const set = this.deps.setInterval ?? setInterval;
    this.timer = set(() => this.setState({ seconds: this.state.seconds + 1 }), 1000);
    this.setState({ status: 'recording', seconds: 0 });
  }

  // Stop recording and move to preview (the clip lands via onstop).
  stop(): void {
    if (this.state.status !== 'recording' || !this.recorder) return;
    try {
      this.recorder.stop();
    } catch {
      this.stopTimer();
      this.releaseStream();
      this.recorder = null;
      this.setState({ status: 'error', errorKind: 'failed' });
    }
  }

  // Discard everything (recording, preview, or error) and return to idle.
  cancel(): void {
    if (this.state.status === 'recording' && this.recorder) {
      this.discardOnStop = true;
      try {
        this.recorder.stop();
      } catch {
        this.stopTimer();
        this.releaseStream();
        this.recorder = null;
        this.setState({ status: 'idle', seconds: 0, clip: null, errorKind: null });
      }
      return;
    }
    this.stopTimer();
    this.releaseStream();
    this.recorder = null;
    this.chunks = [];
    this.setState({ status: 'idle', seconds: 0, clip: null, errorKind: null });
  }

  private stopTimer(): void {
    if (this.timer != null) {
      const clear = this.deps.clearInterval ?? clearInterval;
      clear(this.timer);
      this.timer = null;
    }
  }

  private releaseStream(): void {
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
  }
}

export function formatVoiceSeconds(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(s / 60);
  const seconds = s % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}
